import { z } from 'zod';
import { apiGet } from '@/lib/api';

/**
 * KARTA PRACOWNIKA — jeden odczyt składający to, co portal pokazuje na pięciu ekranach.
 *
 * ⚠️ JEDNO ŻĄDANIE, NIE SZEŚĆ. Ekran ma odpowiadać na pytanie „czemu on nie może pobrać rękawic",
 * a częściowa odpowiedź jest tu gorsza niż brak: gdyby limity dojechały, a dostęp do produktów nie,
 * karta pokazałaby limit bez wpisu dostępu i wyglądałoby to jak konfiguracja, której nie ma.
 *
 * ⚠️ CZAS WYDANIA JAKO NAPIS BEZ STREFY — jak wszędzie. To odczyt zegara maszyny, nie chwila;
 * `new Date(...)` na nim przesunąłby godzinę o strefę przeglądarki.
 */

const ZakresSchema = z.object({
  kind: z.string(),
  id: z.string().nullable(),
  name: z.string(),
  spsKey: z.string(),
});

/** Reguła limitu obejmująca tę osobę — razem z JEJ WŁASNYM zużyciem w oknie tej reguły. */
export const LimitNaKarcieSchema = z.object({
  id: z.string(),
  zakres: ZakresSchema,
  /** Skąd się bierze: reguła na tę osobę czy na jej grupę. */
  zrodlo: z.object({ kind: z.enum(['employee', 'group']), id: z.string().nullable(), name: z.string() }),
  maxQty: z.number(),
  period: z.number(),
  periodType: z.number(),
  oknoLabel: z.string(),
  /** `true` = SPSUI tego okresu nie egzekwuje (miesiąc, rok) — reguła istnieje, ale nie działa. */
  nieegzekwowany: z.boolean(),
  pobrano: z.number(),
  wydan: z.number(),
  zostalo: z.number(),
});

export type LimitNaKarcie = z.infer<typeof LimitNaKarcieSchema>;

export const EmployeeCardSchema = z.object({
  pracownik: z.object({
    id: z.string(),
    groupId: z.string(),
    usunietaZAutomatow: z.string().nullish(),
    usunietaZAutomatowWezel: z.string().nullish(),
    /** Znacznik HR+ (12.10.2026); `null`/brak — wyspa bez HR+ albo starsza instancja. */
    hrPlus: z.string().nullish(),
    hrPlusNumer: z.string().nullish(),
    /** Wyjątek od „Blokuj osoby spoza HR+" przy osobie i czy HR+ ją prowadzi (12.10.2026). */
    hrPlusWyjatek: z.boolean().nullish(),
    hrPlusPowiazana: z.boolean().nullish(),
    spsUserNumber: z.string(),
    name: z.string(),
    alias: z.string().nullable(),
    costCenter: z.string().nullable(),
    active: z.boolean(),
    rationEffect: z.number(),
    /**
     * ⚠️ BEZ TEJ FLAGI DATY NIC NIE ZNACZĄ. SPS wypełnia `DATERANGESTART`/`END` u wszystkich,
     * a egzekwuje zakres tylko przy `DATERANGEENABLE = 1` — patrz komentarz po stronie API.
     */
    dateRangeEnable: z.boolean(),
    dateRangeStart: z.string().nullable(),
    dateRangeEnd: z.string().nullable(),
    /** Pary (pole, kolumna SPS, wartość) — ekran pokazuje kolumnę obok etykiety. */
    znaczniki: z.array(z.object({ pole: z.string(), kolumna: z.string(), wlaczony: z.boolean() })),
    pending: z.boolean(),
  }),
  /** Dwie postacie centrum kosztowego — SPS trzyma obie i obie stempluje na wydaniu. */
  centraKosztowe: z.object({
    zKolumny: z.string().nullable(),
    zEncji: z.array(z.object({ id: z.string(), code: z.string(), name: z.string() })),
  }),
  grupy: z.array(z.object({ id: z.string(), name: z.string(), spsGroupNumber: z.string(), osob: z.number() })),
  /** WSZYSTKIE automaty wyspy — dostęp to suma wpisu bezpośredniego i wpisów grup. */
  automaty: z.array(
    z.object({
      id: z.string(),
      vmid: z.number(),
      name: z.string(),
      online: z.boolean(),
      bezposrednio: z.boolean(),
      zGrup: z.array(z.string()),
    }),
  ),
  operacje: z.object({
    kody: z.array(
      z.object({
        code: z.string(),
        wlasny: z.boolean(),
        zGrup: z.array(z.string()),
        wSlowniku: z.boolean(),
      }),
    ),
    pozaSlownikiem: z.array(z.string()),
  }),
  limity: z.object({
    reguly: z.array(LimitNaKarcieSchema),
    /** Reguły na zadanie/automat — nie da się ich przypisać do osoby, ale mogą obowiązywać. */
    pozaZasiegiem: z.number(),
  }),
  uprawnienia: z.object({
    wpisy: z.array(
      z.object({
        id: z.string(),
        zakres: ZakresSchema,
        zrodlo: z.object({ kind: z.string(), id: z.string().nullable(), name: z.string() }),
        zawezone: z.array(z.string()),
        maLimit: z.boolean(),
      }),
    ),
    nierozpoznane: z.array(
      z.object({ id: z.string(), dConsumer: z.string(), dItem: z.string(), rItem: z.string() }),
    ),
  }),
  aktywnosc: z.object({
    oknoDni: z.number(),
    wydan: z.number(),
    sztuk: z.number(),
    /** NAPIS, nie liczba: `Decimal(12,4)` przez float traci grosze, a to pieniądze. */
    wartosc: z.string(),
    zwrotow: z.number(),
    ostatnie: z.string().nullable(),
    pierwsze: z.string().nullable(),
    automatow: z.number(),
  }),
  ostatnieWydania: z.array(
    z.object({
      id: z.string(),
      ts: z.string(),
      transCode: z.string(),
      qty: z.number(),
      unitCost: z.string(),
      machine: z.object({ vmid: z.number(), name: z.string() }),
      item: z.object({ id: z.string(), itemNumber: z.string(), name: z.string() }).nullable(),
      costCenterAtIssue: z.string().nullable(),
    }),
  ),
  /** Wydania z tym numerem karty, których ingest nie dowiązał do pracownika — liczba NAZWANA. */
  wydaniaBezPrzypisania: z.number(),
});

export type EmployeeCard = z.infer<typeof EmployeeCardSchema>;

export const fetchEmployeeCard = (id: string, groupId: string) =>
  apiGet(`/employees/${id}/card?groupId=${groupId}`, EmployeeCardSchema);
